import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from '../../../Components/TanstackTable'
import useReportTable from '../useReportTable'
import { AgentContactStatusData, AgentContactStatusProps } from './types'
import { getContactStatusOveralls } from './utils'

const AgentContactStatusReportTable = (props: AgentContactStatusProps) => {
    const { reportsData, isLoading } = props
    const { t } = useTranslation()

    const reports = useMemo(() => reportsData?.data || [], [reportsData])
    const contactStatuses = useMemo(() => reports[0]?.contactStatusTotals || [], [reports])

    const columns = useMemo<ColumnDef<AgentContactStatusData>[]>(() => {
        const agentColumn: ColumnDef<AgentContactStatusData> = {
            id: 'agent',
            header: t('app.common.agent'),
            enableSorting: false,
            accessorFn: (row) => `${row.agent?.firstName} ${row.agent?.lastName}`,
        }

        const statusColumns: ColumnDef<AgentContactStatusData>[] = contactStatuses?.map(({ name, statusId }) => ({
            id: `${statusId}`,
            header: name,
            enableSorting: false,
            cell: (info) => {
                const contactStatusTotal = info.row.original.contactStatusTotals?.find((ct) => ct?.statusId === statusId)
                if (!contactStatusTotal) return null
                return <div className="text-center">{contactStatusTotal.total}</div>
            },
        }))

        const totalColumn: ColumnDef<AgentContactStatusData> = {
            id: 'total_by_agent',
            header: t('app.common.total'),
            enableSorting: false,
            cell: (info) => <div className="text-center fw-bolder">{info.row.original.agentGrandTotal}</div>,
        }

        return [agentColumn, ...statusColumns, totalColumn]
    }, [contactStatuses, t])

    const tableFooter = useMemo(() => {
        const { contactStatusOveralls, grandOverall } = getContactStatusOveralls(reports, contactStatuses)

        return (
            <tr>
                <td>{t('app.common.overall')}</td>
                {contactStatusOveralls.map((overall, i) => (
                    <td key={i} className="text-center">
                        {overall.count}
                    </td>
                ))}
                <td className="text-center fw-bolder">{grandOverall}</td>
            </tr>
        )
    }, [reports, contactStatuses, t])

    const table = useReportTable(columns, reports)

    return (
        <DataTable
            table={table}
            tableFooter={tableFooter}
            isLoading={isLoading}
            theadClass="dmn-table-header"
            autoLayout
        />
    )
}

export default AgentContactStatusReportTable
